import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Tag,
  Button,
  Input,
  Spin,
  Table,
  Empty,
  Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  FireOutlined,
  BookOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  SearchOutlined,
  CloseCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { api } from "@/services/api";
import {
  API_ENDPOINTS,
  STATUS_COLORS,
  ASSIGNMENT_STATUS_COLORS,
} from "@/constants";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { formatRelative, formatShortDate } from "@/utils/date";
import type { ApiResponse, StudentAnalytics, DeckAssignment } from "@/types";

const { Title, Text } = Typography;

interface StudentDetailData extends StudentAnalytics {
  name?: string;
  email?: string;
  gradeIds?: number[];
  status?: string;
  assignments?: DeckAssignment[];
}

export default function StudentDetailPage() {
  const { id: studentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [deckSearch, setDeckSearch] = useState('');

  const { data: analyticsResponse, isLoading } = useQuery({
    queryKey: ["teacher", "student-analytics", studentId],
    queryFn: () =>
      api.get<never, ApiResponse<StudentDetailData>>(
        `${API_ENDPOINTS.ANALYTICS_STUDENTS}/${studentId}`,
      ),
    enabled: Boolean(studentId),
  });

  const student = analyticsResponse?.data;

  const filteredAssignments = useMemo(() => {
    const all = student?.assignments ?? [];
    if (!deckSearch.trim()) return all;
    const lower = deckSearch.toLowerCase();
    return all.filter((a) =>
      a.deck?.topicName?.toLowerCase().includes(lower),
    );
  }, [student?.assignments, deckSearch]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!student) {
    return (
      <div>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/teacher/students")}
          style={{ marginBottom: 16 }}
        >
          Back to Students
        </Button>
        <Empty description="Student not found" />
      </div>
    );
  }

  const statusColor = STATUS_COLORS[student.status ?? "ACTIVE"] ?? "#94a3b8";

  const assignmentColumns: ColumnsType<DeckAssignment> = [
    {
      title: "Deck",
      key: "deck",
      render: (_, record) => (
        <Text strong>{record.deck?.topicName ?? "Unknown Deck"}</Text>
      ),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status: DeckAssignment["status"]) => {
        const style = ASSIGNMENT_STATUS_COLORS[status];
        return (
          <Tag
            style={
              style
                ? { color: style.color, background: style.bg, border: "none" }
                : undefined
            }
          >
            {status.replace("_", " ")}
          </Tag>
        );
      },
    },
    {
      title: "Assigned",
      key: "assignedAt",
      render: (_, record) => (
        <Text type="secondary">{formatShortDate(record.assignedAt)}</Text>
      ),
    },
    {
      title: "Deadline",
      key: "deadline",
      render: (_, record) => (
        <Text type="secondary">
          {record.deadline ? formatShortDate(record.deadline) : "—"}
        </Text>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/teacher/students")}
        style={{ marginBottom: 16 }}
      >
        Back to Students
      </Button>

      {/* Profile card */}
      <Card style={{ borderRadius: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "linear-gradient(135deg, #22c55e, #16a34a)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              fontWeight: 700,
              color: "#fff",
              flexShrink: 0,
            }}
          >
            {student.name?.charAt(0)?.toUpperCase() ?? "S"}
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {student.name}
            </Title>
            <Space size="small" wrap style={{ marginTop: 4 }}>
              <Tag
                style={{
                  background: statusColor + "20",
                  color: statusColor,
                  border: "none",
                  fontWeight: 600,
                }}
              >
                {student.status}
              </Tag>
              {(student.gradeIds ?? []).map((g) => (
                <Tag key={g}>{g === 0 ? "KG" : `Grade ${g}`}</Tag>
              ))}
            </Space>
            {student.lastActiveAt && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Last active {formatRelative(student.lastActiveAt)}
                </Text>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Scope note */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, color: "#8c8c8c", fontSize: 13 }}>
        <InfoCircleOutlined />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Analytics and decks below reflect only your assignments for this student.
        </Text>
      </div>

      {/* Stat cards — 2 per row on mobile, 4 on desktop */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 12 }}>
            <Statistic
              title="Streak"
              value={student.currentStreak}
              suffix="days"
              prefix={<FireOutlined style={{ color: "#f59e0b" }} />}
              valueStyle={{ color: "#f59e0b", fontSize: isMobile ? 22 : 28 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 12 }}>
            <Statistic
              title="Cards Reviewed"
              value={student.totalCardsReviewed}
              prefix={<BookOutlined style={{ color: "#6366f1" }} />}
              valueStyle={{ color: "#6366f1", fontSize: isMobile ? 22 : 28 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 12 }}>
            <Statistic
              title="Comprehension"
              value={Math.round(student.averageComprehensionRate * 100)}
              suffix="%"
              prefix={<CheckCircleOutlined style={{ color: "#16a34a" }} />}
              valueStyle={{ color: "#16a34a", fontSize: isMobile ? 22 : 28 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 12 }}>
            <Statistic
              title="Completed"
              value={student.totalDecksCompleted}
              suffix={`/ ${student.totalDecksAssigned}`}
              prefix={<TrophyOutlined style={{ color: "#0891b2" }} />}
              valueStyle={{ color: "#0891b2", fontSize: isMobile ? 22 : 28 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Assigned Decks */}
      <Card title="Decks Assigned by You" style={{ borderRadius: 12 }}>
        <Input
          placeholder="Search by deck name..."
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={deckSearch}
          onChange={(e) => setDeckSearch(e.target.value)}
          suffix={
            deckSearch ? (
              <CloseCircleFilled
                style={{ fontSize: 16, color: '#8c8c8c', cursor: 'pointer' }}
                onClick={() => setDeckSearch('')}
              />
            ) : null
          }
          style={{ maxWidth: isMobile ? '100%' : 360, marginBottom: 16 }}
        />
        {filteredAssignments.length === 0 ? (
          <Empty description={deckSearch.trim() ? `No decks found matching "${deckSearch}"` : "No decks assigned to this student yet."} />
        ) : isMobile ? (
          /* ── Mobile list ── */
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredAssignments.map((record) => {
              const statusStyle = ASSIGNMENT_STATUS_COLORS[record.status];
              return (
                <div
                  key={record.id}
                  style={{
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: "1px solid #f0f0f0",
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 14, flex: 1 }}>
                      {record.deck?.topicName ?? "Unknown Deck"}
                    </Text>
                    <Tag
                      style={
                        statusStyle
                          ? { color: statusStyle.color, background: statusStyle.bg, border: "none", flexShrink: 0, margin: 0 }
                          : { margin: 0 }
                      }
                    >
                      {record.status.replace("_", " ")}
                    </Tag>
                  </div>
                  <div style={{ display: "flex", gap: 20 }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Assigned</Text>
                      <Text style={{ fontSize: 13 }}>{formatShortDate(record.assignedAt)}</Text>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Deadline</Text>
                      <Text style={{ fontSize: 13 }}>
                        {record.deadline ? formatShortDate(record.deadline) : "—"}
                      </Text>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Desktop table ── */
          <Table
            columns={assignmentColumns}
            dataSource={filteredAssignments}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: "No decks assigned to this student yet." }}
          />
        )}
      </Card>
    </div>
  );
}
